import { ACTION_TYPES, HTTP_STATUS } from '~/core/constants'
import { logError } from '~/core/logger'
import { translate } from '~/core/translate'
import { showErrorNotification, showInlineNotification } from '~/core/ui'
import { getItemDetails, getSearchText } from '~/selectors/salesforce'
import { getViewedSessionId } from '~/selectors/ui'
import analytics from '~/services/analytics'
import api from '~/services/api'
import salesforce from '~/services/salesforce'

export const setSearchTerm = payload => ({
  type: ACTION_TYPES.SALESFORCE.SET_SEARCH_TEXT,
  payload
})

const setLoginState = payload => ({
  type: ACTION_TYPES.SALESFORCE.SET_IS_LOGGED_IN,
  payload
})

const setHasPermissions = payload => ({
  type: ACTION_TYPES.SALESFORCE.SET_HAS_PERMISSIONS,
  payload
})

const setUserInfo = payload => ({
  type: ACTION_TYPES.SALESFORCE.SET_USER_INFO,
  payload
})

export const handleSalesforceLoginResponse = notification => {
  if (notification.error) {
    return setLoginState(false)
  }

  return setLoginState(true)
}

export const retrieveUserInfo = () => async dispatch => {
  const response = await salesforce.fetchUserInfo()

  if (!api.isErrorResponse(response)) {
    dispatch(setUserInfo(response.email))
  }
}

export const logout = () => async dispatch => {
  await salesforce.logout()

  dispatch(setLoginState(false))

  analytics.trackEvent(analytics.EVENT_NAMES.SALESFORCE_LOGOUT)
}

export const login = () => async (dispatch, getState) => {
  try {
    const response = await salesforce.fetchLoginUrl()

    if (!api.isErrorResponse(response)) {
      await salesforce.login({ loginUrl: response.url })
      dispatch(setLoginState(true))

      analytics.trackEvent(analytics.EVENT_NAMES.SALESFORCE_LOGIN)
      return true
    }
  } catch (err) {
    dispatch(setLoginState(false))
  }
}

export const getLoginState = () => async dispatch => {
  const isLoggedIn = await salesforce.checkLoginStatus()

  return dispatch(setLoginState(isLoggedIn))
}

export const addRecordToMeeting = ({ notes, recordId }) => async (dispatch, getState) => {
  const sessionId = getViewedSessionId(getState())
  const { type } = getItemDetails(recordId)(getState())

  showInlineNotification(translate('salesforce_adding_meeting'))

  const response = await salesforce.updateSessionWithRecord({ notes, sessionId, recordId, recordType: type })

  if (api.isErrorResponse(response)) {
    if (response.status === HTTP_STATUS.CONFLICT) {
      showErrorNotification(translate('salesforce_add_failed_already_linked'))
    } else if (response.status === HTTP_STATUS.FORBIDDEN) {
      showErrorNotification(translate('salesforce_add_failed_no_permission'))
    } else {
      showErrorNotification(translate('salesforce_add_failed'))
    }

    logError(
      { __filename },
      'Could not update session with salesforce record'
    )
  } else {
    showInlineNotification(translate('salesforce_added_meeting'))

    dispatch(retrieveSessionRecords(sessionId))

    analytics.trackEvent(analytics.EVENT_NAMES.SALESFORCE_LINK)
  }
}

export const removeRecordFromMeeting = ({ recordId }) => async (dispatch, getState) => {
  const sessionId = getViewedSessionId(getState())
  const { type } = getItemDetails(recordId)(getState())

  const response = await salesforce.deleteRecordFromSession({ sessionId, recordId, recordType: type })

  if (api.isErrorResponse(response)) {
    showErrorNotification(translate('salesforce_remove_meeting_failed'))

    logError(
      { __filename },
      'Could not remove salesforce record from session'
    )
  } else {
    dispatch({
      type: ACTION_TYPES.SALESFORCE.REMOVE_ITEM,
      payload: { recordId, sessionId }
    })

    analytics.trackEvent(analytics.EVENT_NAMES.SALESFORCE_UNLINK)
  }
}

export const searchRecords = () => async (dispatch, getState) => {
  dispatch({
    type: ACTION_TYPES.SALESFORCE.FETCHING_SEARCH_RECORDS
  })

  const searchText = getSearchText(getState())
  const response = await salesforce.fetchRecords({ searchText })

  if (api.isErrorResponse(response)) {
    logError(
      { __filename },
      'Failed searching salesforce records'
    )
    dispatch({
      type: ACTION_TYPES.SALESFORCE.FETCH_SEARCH_RECORDS_ERROR
    })
  } else {
    dispatch({
      type: ACTION_TYPES.SALESFORCE.FETCHED_SEARCH_RECORDS,
      payload: response
    })
  }
}

export const retrieveSessionRecords = sessionId => async dispatch => {
  const response = await salesforce.fetchSessionRecords({ sessionId })

  if (api.isErrorResponse(response)) {
    if (response.status === HTTP_STATUS.FORBIDDEN) {
      dispatch(setHasPermissions(false))
    }

    logError(
      { __filename },
      'Failed retrieving session records'
    )

    return false
  } else {
    dispatch({
      type: ACTION_TYPES.SALESFORCE.FETCHED_SESSION_TAGS,
      payload: { tags: response, sessionId }
    })

    return true
  }
}

export const retrieveRecents = () => async dispatch => {
  const response = await salesforce.fetchRecentRecords()

  if (api.isErrorResponse(response)) {
    logError(
      { __filename },
      'Could not retrieve recent salesforce records'
    )
  } else {
    dispatch({
      type: ACTION_TYPES.SALESFORCE.FETCHED_RECENTS,
      payload: response
    })
  }
}

export const retrieveRecordDetails = recordId => async (dispatch, getState) => {
  const { type, hasDetails } = getItemDetails(recordId)(getState())

  if (hasDetails) {
    return
  }

  const response = await salesforce.fetchRecordDetails({ recordId, type })

  if (api.isErrorResponse(response)) {
    logError(
      { __filename, recordId },
      'Could not retrieve salesforce record details'
    )
  } else {
    dispatch({
      type: ACTION_TYPES.SALESFORCE.FETCHED_ITEM_DETAILS,
      payload: response
    })
  }
}
